import type { SerializableRecord } from '../model';
import { transformContentfulSys } from './transformContentfulSys';
import { isSerializableRecord } from './isSerializableRecord';
import { compactInternalPageReference } from './compactInternalPageReference';
import { transformInternalAnchorReference } from './transformInternalAnchorReference';
import {
  type ContentfulFile,
  transformContentfulFile
} from './transformContentfulFile';
import { getContentTypeId } from './getContentTypeId';

const LINK_STRING_FIELDS = [
  'linkText',
  'linkTarget',
  'externalUrl',
  'queryParameter',
  'linkStyle',
  'linkStyleSize',
  'linkIcon',
  'linkIconPosition',
  'typeformForm',
  'documentId',
  'softwareId',
  'title',
  'slug'
] as const;

/**
 * Strips a resolved Contentful link/download (or compact content page) down to
 * the fields required by `buildLinkInterface` and `ContentfulLink`.
 */
export function compactLinkReference(
  value: SerializableRecord
): SerializableRecord | undefined {
  const contentTypeId = getContentTypeId(value);

  if (contentTypeId === 'contentPage' || contentTypeId === 'download') {
    return compactInternalPageReference(value);
  }

  if (contentTypeId !== 'link') {
    return undefined;
  }

  const sys = transformContentfulSys(value.sys);
  const fields = isSerializableRecord(value.fields) ? value.fields : undefined;

  if (!sys || !fields) {
    return undefined;
  }

  const compactedFields: SerializableRecord = {};

  for (const key of LINK_STRING_FIELDS) {
    const fieldValue = fields[key];
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      compactedFields[key] = fieldValue;
    }
  }

  if (isSerializableRecord(fields.internalPage)) {
    const internalPage = compactInternalPageReference(fields.internalPage);
    if (internalPage) {
      compactedFields.internalPage = internalPage;
    }
  }

  if (isSerializableRecord(fields.internalAnchor)) {
    const internalAnchor = transformInternalAnchorReference(
      fields.internalAnchor
    );
    if (internalAnchor) {
      compactedFields.internalAnchor = internalAnchor;
    }
  }

  if (isSerializableRecord(fields.asset)) {
    const asset = fields.asset;
    const assetSys = transformContentfulSys(asset.sys);
    const assetFields = isSerializableRecord(asset.fields)
      ? asset.fields
      : undefined;

    if (assetSys && assetFields && isSerializableRecord(assetFields.file)) {
      compactedFields.asset = {
        sys: assetSys,
        fields: {
          title: assetFields.title,
          description: assetFields.description,
          file: transformContentfulFile(assetFields.file as ContentfulFile)
        }
      };
    }
  }

  return {
    sys,
    fields: compactedFields
  };
}
